/**
 * QuizHeader — 표준 퀴즈 헤더.
 * QuizTypeBadge + <h3> 제목 + 옵셔널 problem 슬롯 + QuizMedia.
 * (특수 레이아웃 유형은 QuizTypeBadge만 직접 사용한다.)
 */
import type { ReactNode } from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../../lib/utils'
import type { QuizMediaObject } from '../../../types/legacyQuiz'
import { KatexText } from './katex-text'
import { QuizMedia } from './quiz-media'
import { QuizTypeBadge } from './quiz-type-badge'
import { PolygonRenderer, parsePolygonTitle } from './polygon-renderer'
import { useQuizStyleVariant } from './quiz-style-context'

interface QuizHeaderProps {
  quizId: string | number
  typeLabel: string
  title: string
  difficultyLevel?: number | null
  variant?: 'adult' | 'student'
  image?: QuizMediaObject | null
  imageMobile?: QuizMediaObject | null
  desmos?: string | null
  vars?: Record<string, unknown>
  /** h3와 QuizMedia 사이에 들어가는 본문 (유형별 JSX). 없으면 미렌더. */
  problem?: ReactNode
  dimTitleText?: boolean
  dimProblemText?: boolean
  dimMedia?: boolean
}

export function QuizHeader({
  quizId,
  typeLabel,
  title,
  difficultyLevel,
  variant = 'adult',
  image,
  imageMobile,
  desmos,
  vars,
  problem,
  dimTitleText = false,
  dimProblemText = false,
  dimMedia = false,
}: QuizHeaderProps) {
  const styleVariant = useQuizStyleVariant()
  const isLcStudent = styleVariant === 'lc' && variant === 'student'
  const isAiqStudent = styleVariant === 'aiq' && variant === 'student'

  return (
    <motion.div
      data-quiz-header
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'space-y-2',
        isLcStudent && '[&_[data-quiz-media]]:!mt-6 [&_[data-quiz-media]>div]:!my-0',
      )}
    >
      <QuizTypeBadge quizId={quizId} typeLabel={typeLabel} difficultyLevel={difficultyLevel} variant={variant} />
      {(() => {
        const polygonCounts = parsePolygonTitle(title)
        return polygonCounts ? (
          <PolygonRenderer counts={polygonCounts} />
        ) : (
          <h3
            data-quiz-title-text
            className={cn(
              isLcStudent
                ? 'text-[20px] font-semibold leading-[1.45] tracking-[-0.1px] text-lc-text-primary'
                : isAiqStudent
                  ? 'text-[22px] font-medium leading-normal text-lc-text-primary'
                  : 'text-2xl font-bold leading-relaxed text-gray-900',
              dimTitleText && 'opacity-[0.05]',
            )}
          >
            <KatexText text={title} />
          </h3>
        )
      })()}
      {problem && (
        <div data-quiz-problem-text className={cn(dimProblemText && 'opacity-[0.05]')}>
          {problem}
        </div>
      )}
      <QuizMedia
        image={image}
        imageMobile={imageMobile}
        desmos={desmos}
        vars={vars}
        className={cn(dimMedia && 'opacity-[0.05]')}
      />
    </motion.div>
  )
}
